/* it7-fx.js — real Shopify Markets prices for JS-rendered surfaces (no frontend conversion).

   WHY THIS EXISTS
   ---------------
   When the shopper changes market via the localization selector the page reloads and
   Shopify SSR regenerates every price from the market's price list — correct, untouched.
   But prices rendered by JavaScript after load (PLP filter/sort/load-more, search,
   PDP "More Like This", header type-ahead, wishlist) come from XCloud (CloudSearch),
   a third-party index that returns BASE (shop) currency = USD regardless of market.
   We must NOT convert those on the client. Instead we fetch the REAL Markets price for
   each product from Shopify and display it as-is.

   HOW (token-free)
   ----------------
   IT7FX.quote(handle) -> { price, compareAt } in the active market's presentment
   currency, fetched from /products/{handle}?view=fx — the product.fx alternate
   template, which renders in the active Markets context (so product.price IS the
   Markets price). Results are cached per handle for the session.

   The ONLY scaled value here is IT7FX.toBase(): it maps the shopper's typed price-
   FILTER range into the base units XCloud filters on (the index is base-only). That
   rate is learned from Shopify itself (one reference product: base ÷ presentment)
   and is never used to render a displayed product price.

   Primary/USD markets short-circuit (presentment === false): base already equals the
   displayed currency, so surfaces skip quote() and no requests are made. Any failure
   degrades safely (quote -> null, rate -> 1), and surfaces fall back to base.

   Loaded NON-deferred by it7-header so window.IT7FX exists before later section scripts. */
window.IT7FX = (function () {
  var el = document.getElementById('it7fx-data');
  var ds = (el && el.dataset) || {};
  var COUNTRY = ds.country || '';
  var ISO = ds.iso || 'USD';
  var SYM = ds.sym || '$';
  var SHOP_ISO = ds.shop || 'USD';

  function round(n) { return Math.round(Number(n) || 0).toLocaleString('en-US'); }

  var api = {
    country: COUNTRY,
    iso: ISO,
    sym: SYM,
    shopIso: SHOP_ISO,
    /* true when the active market is not the shop currency, i.e. prices must come
       from Markets rather than being passed through as base. */
    presentment: ISO !== SHOP_ISO,
    /* format an amount that is ALREADY in the display currency — no conversion. */
    format: function (major) { return SYM + round(major) + ' ' + ISO; },
    formatBare: function (major) { return SYM + round(major); },
    /* filter-range rate (presentment ÷ base); 1 until learned / for primary market. */
    rate: 1,
    ready: null,
    /* shopper-typed presentment amount -> base units, for the XCloud price filter only. */
    toBase: function (major) { return Number(major) / (api.rate || 1); }
  };

  /* real per-product Markets price (presentment), cached per handle. */
  var qcache = {};
  api.quote = function (handle) {
    if (!handle) return Promise.resolve(null);
    if (qcache[handle]) return qcache[handle];
    var p = fetch('/products/' + handle + '?view=fx', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('fx ' + r.status); return r.json(); })
      .then(function (j) {
        return {
          price: (Number(j.price) || 0) / 100,                 /* presentment, major units */
          compareAt: (Number(j.compare_at_price) || 0) / 100,
          currency: j.currency
        };
      })
      .catch(function () { return null; });
    qcache[handle] = p;
    return p;
  };

  /* primary / USD market: no conversion, no rate learning, no requests. */
  if (!api.presentment) { api.ready = Promise.resolve(1); return api; }

  /* learn the filter-range rate once per session (base ÷ presentment). */
  var KEY = 'it7fx_rate_' + COUNTRY;
  var cached = parseFloat(sessionStorage.getItem(KEY));
  if (cached > 0) { api.rate = cached; api.ready = Promise.resolve(cached); return api; }

  api.ready = fetch('/products.json?limit=1', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
    .then(function (r) { return r.json(); })
    .then(function (j) {
      var p = j && j.products && j.products[0];
      var v = p && p.variants && p.variants[0];
      var base = v ? parseFloat(v.price) : 0;   /* /products.json price = shop currency, major units */
      if (!p || !p.handle || !(base > 0)) throw new Error('no reference product');
      return api.quote(p.handle).then(function (q) {
        if (q && q.price > 0) {
          var f = q.price / base;
          if (f > 0 && isFinite(f)) {
            api.rate = f;
            try { sessionStorage.setItem(KEY, String(f)); } catch (e) {}
          }
        }
        return api.rate;
      });
    })
    .catch(function () { api.rate = 1; return 1; });

  return api;
})();
